import React, { useEffect, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/LayersClear';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { Attachment, AttachmentError, readAttachment, SKETCH_DESCRIPTION } from '../../assistant/attachments';
import { captureViewportPngOrNull } from '../../three/reactThreeFiberBindings';

interface Point {
  readonly x: number;
  readonly y: number;
}

interface Stroke {
  readonly color: string;
  readonly points: readonly Point[];
}

// Bright enough to stand out from the truss's own colors on the dark viewport.
const PEN_COLORS = ['#ff3b30', '#ffcc00', '#34c759', '#ffffff'];

function drawStroke(context: CanvasRenderingContext2D, stroke: Stroke, width: number) {
  context.strokeStyle = stroke.color;
  context.fillStyle = stroke.color;
  context.lineWidth = width;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  const [first, ...rest] = stroke.points;
  if (rest.length === 0) {
    context.beginPath();
    context.arc(first.x, first.y, width / 2, 0, Math.PI * 2);
    context.fill();
    return;
  }
  context.beginPath();
  context.moveTo(first.x, first.y);
  rest.forEach((point) => context.lineTo(point.x, point.y));
  context.stroke();
}

function readBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob == null ? reject(new Error('Empty sketch')) : resolve(blob)), 'image/png'),
  );
}

/**
 * Lets the designer draw over a screenshot of the view, to show the Assistant what they mean. The sketch is attached
 * to their next message as an image.
 */
export function SketchDialog({
  open,
  sketchNumber,
  onClose,
  onAttach,
}: {
  open: boolean;
  sketchNumber: number;
  onClose: () => void;
  onAttach: (attachment: Attachment) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [screenshot, setScreenshot] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [strokes, setStrokes] = useState<readonly Stroke[]>([]);
  const [color, setColor] = useState(PEN_COLORS[0]);
  const drawing = useRef<Stroke | null>(null);

  // Capture the view as the dialog opens, before the dialog's backdrop dims it.
  useEffect(() => {
    if (!open) return;
    setStrokes([]);
    setError(null);
    setScreenshot(null);
    const dataUrl = captureViewportPngOrNull();
    if (dataUrl == null) {
      setError('The view isn’t ready to capture yet.');
      return;
    }
    const image = new Image();
    image.onload = () => setScreenshot(image);
    image.onerror = () => setError('Couldn’t capture the view.');
    image.src = dataUrl;
  }, [open]);

  // Pens scale with the screenshot, so a stroke looks the same however large the window is.
  const penWidth =
    screenshot == null ? 4 : Math.max(3, Math.round(Math.max(screenshot.width, screenshot.height) / 300));

  const redraw = (extra?: Stroke) => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas == null || context == null || screenshot == null) return;
    // The viewport can be transparent, so give it the app's dark background.
    context.fillStyle = '#121212';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(screenshot, 0, 0);
    [...strokes, ...(extra ? [extra] : [])].forEach((stroke) => drawStroke(context, stroke, penWidth));
  };

  useEffect(redraw);

  const toCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = event.currentTarget;
    const bounds = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
      y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
    };
  };

  const finishStroke = () => {
    const stroke = drawing.current;
    drawing.current = null;
    if (stroke != null) setStrokes((current) => [...current, stroke]);
  };

  const undo = () => setStrokes((current) => current.slice(0, -1));

  const attach = async () => {
    const canvas = canvasRef.current;
    if (canvas == null) return;
    try {
      const name = `Sketch ${sketchNumber}.png`;
      const blob = await readBlob(canvas);
      // Read like a picked file, so a large sketch is shrunk the same way.
      const attachment = await readAttachment(new File([blob], name, { type: 'image/png' }));
      if (attachment.kind !== 'image') return;
      onAttach({ ...attachment, description: SKETCH_DESCRIPTION });
      onClose();
    } catch (caught) {
      setError(caught instanceof AttachmentError ? caught.message : 'Couldn’t attach the sketch.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      onKeyDown={(event) => {
        // Keep the app's hotkeys, like its own undo, from firing while sketching.
        event.stopPropagation();
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
          event.preventDefault();
          undo();
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Sketch for the Assistant</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Draw on the view to show what you mean.
          </Typography>
          {PEN_COLORS.map((penColor) => (
            <IconButton
              key={penColor}
              size="small"
              aria-label={`Pen color ${penColor}`}
              aria-pressed={penColor === color}
              onClick={() => setColor(penColor)}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: penColor,
                  outline: penColor === color ? 2 : 0,
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                }}
              />
            </IconButton>
          ))}
          <Tooltip title="Undo">
            <span>
              <IconButton aria-label="Undo" disabled={strokes.length === 0} onClick={undo}>
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear">
            <span>
              <IconButton aria-label="Clear" disabled={strokes.length === 0} onClick={() => setStrokes([])}>
                <ClearIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
        {screenshot && (
          <canvas
            ref={canvasRef}
            width={screenshot.width}
            height={screenshot.height}
            aria-label="Sketch"
            style={{
              display: 'block',
              maxWidth: 'calc(100vw - 112px)',
              maxHeight: 'calc(100vh - 240px)',
              cursor: 'crosshair',
              touchAction: 'none',
            }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              drawing.current = { color, points: [toCanvasPoint(event)] };
              redraw(drawing.current);
            }}
            onPointerMove={(event) => {
              if (drawing.current == null) return;
              drawing.current = { ...drawing.current, points: [...drawing.current.points, toCanvasPoint(event)] };
              redraw(drawing.current);
            }}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={screenshot == null} onClick={() => void attach()}>
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
}
